import { useCallback, useEffect, useState } from "react";
import { StorageAPI, type StorageFile, type StorageFileListQuery } from "@repo/api";
import { useAuth } from "@repo/context/auth/auth";

const PAGE_SIZE = 20;

export function useStorageFileList(query?: Omit<StorageFileListQuery, "page" | "pageSize">) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState<StorageFile[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPage = useCallback(
    async (nextPage: number, append: boolean) => {
      if (!isAuthenticated) {
        setItems([]);
        setHasMore(false);
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const { data } = await StorageAPI.listFiles({
          ...query,
          page: nextPage,
          pageSize: PAGE_SIZE,
          sortBy: query?.sortBy ?? "created_at",
          sortOrder: query?.sortOrder ?? "desc",
        });
        const content = data.content ?? [];
        setItems((prev) => (append ? [...prev, ...content] : content));
        setPage(nextPage);
        setHasMore(nextPage < (data.total_pages || 1));
      } catch (e: unknown) {
        const err = e as { msg?: string };
        setError(err.msg || "获取文件列表失败");
        if (!append) {
          setItems([]);
          setHasMore(false);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [isAuthenticated, query],
  );

  useEffect(() => {
    void loadPage(1, false);
  }, [loadPage]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    void loadPage(page + 1, true);
  }, [loading, loadingMore, hasMore, page, loadPage]);

  return {
    items,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    refresh: () => loadPage(1, false),
  };
}
